<template>
  <div class="p-6 space-y-6" style="max-width:960px;">
    <div class="page-header">
      <div>
        <h1 class="page-title">安全信件</h1>
        <p class="page-subtitle">管理透過安全門戶發送的加密信件</p>
      </div>
      <button class="btn btn-primary" @click="showCompose = true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        新建安全信件
      </button>
    </div>

    <!-- Stats bar -->
    <div class="grid gap-4" style="grid-template-columns:repeat(4,1fr);">
      <div class="card p-4 text-center">
        <div class="text-2xl font-bold" style="color:var(--brand-600);">{{ stats.total }}</div>
        <div class="text-xs mt-1" style="color:var(--gray-400);">總計</div>
      </div>
      <div class="card p-4 text-center">
        <div class="text-2xl font-bold" style="color:var(--color-success);">{{ stats.active }}</div>
        <div class="text-xs mt-1" style="color:var(--gray-400);">有效中</div>
      </div>
      <div class="card p-4 text-center">
        <div class="text-2xl font-bold" style="color:var(--color-warning);">{{ stats.opened }}</div>
        <div class="text-xs mt-1" style="color:var(--gray-400);">已開啟</div>
      </div>
      <div class="card p-4 text-center">
        <div class="text-2xl font-bold" style="color:var(--gray-400);">{{ stats.burned + stats.expired + stats.revoked }}</div>
        <div class="text-xs mt-1" style="color:var(--gray-400);">已失效</div>
      </div>
    </div>

    <!-- Message list -->
    <div class="card">
      <div class="p-4 flex items-center gap-3" style="border-bottom:1px solid var(--border);">
        <input v-model="search" class="input" style="max-width:280px;" placeholder="搜尋收件者…">
        <select v-model="filterStatus" class="input" style="max-width:160px;">
          <option value="">全部狀態</option>
          <option value="active">有效中</option>
          <option value="expired">已過期</option>
          <option value="burned">已銷毀</option>
          <option value="revoked">已撤回</option>
        </select>
        <button class="btn btn-ghost btn-sm" @click="fetchMessages" :disabled="loading">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          重新整理
        </button>
      </div>

      <div v-if="loading" class="p-10 text-center" style="color:var(--gray-400);">
        <div class="spinner mx-auto"></div>
        <div class="mt-3 text-sm">載入中…</div>
      </div>

      <div v-else-if="filteredMessages.length === 0" class="p-10 text-center" style="color:var(--gray-400);">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="mx-auto mb-3" style="color:var(--gray-300);"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        <div class="text-sm font-medium">尚無安全信件</div>
        <div class="text-xs mt-1">使用「新建安全信件」傳送加密郵件</div>
      </div>

      <div v-else>
        <div v-for="msg in filteredMessages" :key="msg.id"
          class="flex items-start gap-4 p-4 cursor-pointer"
          style="transition:background 0.15s;border-bottom:1px solid var(--border);"
          @mouseover="$event.currentTarget.style.background='var(--gray-50)'"
          @mouseleave="$event.currentTarget.style.background=''"
          @click="openDetail(msg)">
          <!-- Status icon -->
          <div class="flex-shrink-0 mt-0.5">
            <div class="w-8 h-8 rounded-full flex items-center justify-center" :style="statusIconStyle(msg.status)">
              <svg v-if="msg.status === 'active'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <svg v-else-if="msg.status === 'burned'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              <svg v-else-if="msg.status === 'revoked'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/></svg>
            </div>
          </div>
          <!-- Info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-medium text-sm truncate" style="color:var(--gray-800);">{{ msg.recipient_email }}</span>
              <span class="badge" :class="statusBadge(msg.status)">{{ statusLabel(msg.status) }}</span>
              <span v-if="msg.burn_after_read" class="badge" style="background:var(--color-danger-bg);color:var(--color-danger);">閱後即焚</span>
              <span v-if="msg.watermark" class="badge" style="background:var(--gray-100);color:var(--gray-500);">浮水印</span>
            </div>
            <div class="text-xs mt-1" style="color:var(--gray-400);">
              建立：{{ fmtDate(msg.created_at) }} ·
              到期：{{ fmtDate(msg.expires_at) }} ·
              開啟次數：{{ msg.open_count }} / {{ msg.max_open_count }}
            </div>
          </div>
          <!-- Actions -->
          <div class="flex-shrink-0 flex items-center gap-2" @click.stop>
            <button class="btn btn-ghost btn-xs" title="複製連結" @click="copyPortalUrl(msg.token)">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            </button>
            <button v-if="msg.status === 'active'" class="btn btn-ghost btn-xs" style="color:var(--color-danger);" title="撤回" @click="confirmRevoke(msg)">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail / Access log panel -->
    <div v-if="selectedMsg" class="card p-5 space-y-4">
      <div class="flex items-center justify-between">
        <div class="font-semibold" style="color:var(--gray-700);">存取記錄 — {{ selectedMsg.recipient_email }}</div>
        <button class="btn btn-ghost btn-xs" @click="selectedMsg = null">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div v-if="accessLoading" class="text-sm" style="color:var(--gray-400);">載入中…</div>
      <div v-else-if="accessLogs.length === 0" class="text-sm" style="color:var(--gray-400);">尚無存取記錄</div>
      <div v-else>
        <table class="w-full text-xs">
          <thead>
            <tr style="color:var(--gray-400);border-bottom:1px solid var(--border);">
              <th class="text-left pb-2">事件</th>
              <th class="text-left pb-2">IP</th>
              <th class="text-left pb-2">時間</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in accessLogs" :key="log.id" style="border-bottom:1px solid var(--border);">
              <td class="py-2 pr-4">
                <span class="badge" :class="eventBadge(log.event_type)">{{ eventLabel(log.event_type) }}</span>
              </td>
              <td class="py-2 pr-4 font-mono" style="color:var(--gray-600);">{{ log.ip_address || '—' }}</td>
              <td class="py-2" style="color:var(--gray-500);">{{ fmtDate(log.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Revoke modal -->
    <div v-if="revokeTarget" class="modal-overlay" @click.self="revokeTarget = null">
      <div class="modal-box space-y-4">
        <div class="font-semibold text-base" style="color:var(--gray-800);">撤回安全信件</div>
        <div class="text-sm" style="color:var(--gray-500);">此操作將立即失效連結，並通知收件人。</div>
        <div>
          <label class="block text-sm font-medium mb-1.5" style="color:var(--gray-700);">撤回原因（選填）</label>
          <input v-model="revokeReason" class="input w-full" placeholder="例：資料更新，請使用新連結">
        </div>
        <div class="flex justify-end gap-2">
          <button class="btn btn-ghost" @click="revokeTarget = null">取消</button>
          <button class="btn btn-danger" @click="doRevoke" :disabled="revokeLoading">{{ revokeLoading ? '撤回中…' : '確認撤回' }}</button>
        </div>
      </div>
    </div>

    <!-- New message compose panel -->
    <div v-if="showCompose" class="modal-overlay" @click.self="showCompose = false">
      <div class="sc-modal">
        <!-- Header -->
        <div class="sc-modal__header">
          <div class="sc-modal__title-group">
            <div class="sc-modal__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </div>
            <div>
              <div class="sc-modal__title">新建安全信件</div>
              <div class="sc-modal__subtitle">收件人將透過加密門戶存取此信件</div>
            </div>
          </div>
          <button class="sc-modal__close" @click="showCompose = false" title="關閉">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <!-- Scrollable body -->
        <div class="sc-modal__body">

          <!-- 成功 / 部分成功訊息 -->
          <div v-if="composeSuccess" class="sc-alert" :class="notifyOk ? 'sc-alert--success' : 'sc-alert--warn'">
            <svg v-if="notifyOk" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <div class="sc-alert__content">
              <div class="sc-alert__text">{{ composeSuccess }}</div>
              <div class="sc-alert__url-wrap">
                <code class="sc-alert__url">{{ lastPortalUrl }}</code>
                <button class="btn btn-ghost btn-xs" @click="copyText(lastPortalUrl)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  複製連結
                </button>
              </div>
            </div>
          </div>

          <!-- 錯誤訊息 -->
          <div v-if="composeError" class="sc-alert sc-alert--error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <div class="sc-alert__text">{{ composeError }}</div>
          </div>

          <!-- Section: 收件人 -->
          <div class="sc-section">
            <div class="sc-section__label">收件人信箱 <span class="sc-required">*</span></div>
            <input v-model="compose.to" class="input w-full" placeholder="recipient@example.com" type="email">
          </div>

          <div class="sc-section">
            <div class="sc-section__label">主旨 <span class="sc-required">*</span></div>
            <input v-model="compose.subject" class="input w-full" placeholder="郵件主旨">
          </div>

          <div class="sc-section">
            <div class="sc-section__label">內文 <span class="sc-required">*</span></div>
            <textarea v-model="compose.body" class="input w-full sc-textarea" rows="6" placeholder="郵件內容…"></textarea>
          </div>

          <!-- Section: 安全設定 -->
          <div class="sc-card-section">
            <div class="sc-card-section__title">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              存取控制
            </div>
            <div class="sc-grid2">
              <div class="sc-section">
                <div class="sc-section__label sc-section__label--sm">收件方密碼 <span style="color:#ef4444">*</span></div>
                <input v-model="compose.mfa_password" type="password" class="input w-full" placeholder="請設定密碼，再透過電話或其他管道告知收件人" autocomplete="new-password">
                <div class="sc-hint" style="margin-top:4px">密碼請勿用電子信件傳送，請改用電話、LINE 等方式告知收件人</div>
              </div>
            </div>
            <div class="sc-grid2 sc-grid2--mt">
              <div class="sc-section">
                <div class="sc-section__label sc-section__label--sm">有效期限</div>
                <select v-model="compose.expires_in_hours" class="input w-full">
                  <option :value="1">1 小時</option>
                  <option :value="6">6 小時</option>
                  <option :value="24">24 小時</option>
                  <option :value="48">48 小時</option>
                  <option :value="72">72 小時</option>
                  <option :value="168">7 天</option>
                </select>
              </div>
              <div class="sc-section">
                <div class="sc-section__label sc-section__label--sm">最多開啟次數</div>
                <select v-model="compose.max_open_count" class="input w-full">
                  <option :value="1">1 次</option>
                  <option :value="3">3 次</option>
                  <option :value="5">5 次</option>
                  <option :value="10">10 次</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Section: 內容保護 -->
          <div class="sc-card-section">
            <div class="sc-card-section__title">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              內容保護
            </div>
            <div class="sc-checks">
              <label class="sc-check">
                <input type="checkbox" v-model="compose.burn_after_read" class="sc-check__input">
                <div class="sc-check__body">
                  <div class="sc-check__label">閱後即焚</div>
                  <div class="sc-check__desc">開啟後，信件閱讀完畢即自動銷毀</div>
                </div>
              </label>
              <label class="sc-check">
                <input type="checkbox" v-model="compose.no_download" class="sc-check__input">
                <div class="sc-check__body">
                  <div class="sc-check__label">禁止下載</div>
                  <div class="sc-check__desc">防止收件人下載附件或另存內容</div>
                </div>
              </label>
              <label class="sc-check">
                <input type="checkbox" v-model="compose.watermark" class="sc-check__input">
                <div class="sc-check__body">
                  <div class="sc-check__label">動態浮水印</div>
                  <div class="sc-check__desc">信件顯示時疊加收件人資訊浮水印</div>
                </div>
              </label>
            </div>
          </div>

        </div><!-- /body -->

        <!-- Sticky footer -->
        <div class="sc-modal__footer">
          <button class="btn btn-ghost" @click="showCompose = false">關閉</button>
          <button class="btn btn-primary" @click="sendSecure" :disabled="composeSending">
            <svg v-if="!composeSending" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            <svg v-else width="14" height="14" class="sc-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
            {{ composeSending ? '傳送中…' : '安全傳送' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Secure Compose Modal ── */
.sc-modal {
  background: var(--surface, #fff);
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0,0,0,.22);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.sc-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  flex-shrink: 0;
  background: linear-gradient(135deg, #f0f4ff 0%, #fff 60%);
}
.sc-modal__title-group {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.sc-modal__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--brand-100, #e0e7ff);
  color: var(--brand-600, #4f46e5);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sc-modal__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--gray-900, #111827);
  line-height: 1.3;
}
.sc-modal__subtitle {
  font-size: 0.75rem;
  color: var(--gray-500, #6b7280);
  margin-top: 2px;
}
.sc-modal__close {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  background: var(--gray-100, #f3f4f6);
  color: var(--gray-500, #6b7280);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background .15s;
}
.sc-modal__close:hover { background: var(--gray-200, #e5e7eb); }

/* Scrollable body */
.sc-modal__body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Footer */
.sc-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid var(--border, #e5e7eb);
  flex-shrink: 0;
  background: var(--gray-50, #f9fafb);
}

/* Alert banners */
.sc-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 0.82rem;
}
.sc-alert--success {
  background: var(--color-success-bg, #f0fdf4);
  color: var(--color-success, #15803d);
  border: 1px solid #bbf7d0;
}
.sc-alert--error {
  background: var(--color-danger-bg, #fff1f2);
  color: var(--color-danger, #be123c);
  border: 1px solid #fecdd3;
}
.sc-alert--warn {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}
.sc-alert svg { flex-shrink: 0; margin-top: 1px; }
.sc-alert__content { flex: 1; min-width: 0; }
.sc-alert__text { line-height: 1.5; }
.sc-alert__url-wrap {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(0,0,0,.04);
  flex-wrap: wrap;
}
.sc-alert__url {
  font-family: monospace;
  font-size: 0.78rem;
  color: var(--gray-700, #374151);
  word-break: break-all;
  flex: 1;
  line-height: 1.5;
}

/* Form sections */
.sc-section { display: flex; flex-direction: column; gap: 5px; }
.sc-section__label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--gray-700, #374151);
}
.sc-section__label--sm { font-size: 0.76rem; color: var(--gray-600, #4b5563); }
.sc-required { color: #ef4444; }
.sc-textarea { resize: vertical; min-height: 120px; font-family: inherit; }

/* Card sections */
.sc-card-section {
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 12px;
  padding: 14px 16px;
  background: var(--gray-50, #f9fafb);
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sc-card-section__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--gray-600, #4b5563);
  text-transform: uppercase;
  letter-spacing: .4px;
}

/* 2-column grid */
.sc-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.sc-grid2--mt { margin-top: 4px; }
.sc-hint {
  font-size: 0.78rem;
  color: var(--gray-400, #9ca3af);
  line-height: 1.5;
  padding-top: 8px;
}

/* Checkboxes */
.sc-checks { display: flex; flex-direction: column; gap: 0; }
.sc-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border, #e5e7eb);
  cursor: pointer;
}
.sc-check:last-child { border-bottom: none; padding-bottom: 0; }
.sc-check:first-child { padding-top: 0; }
.sc-check__input { margin-top: 3px; flex-shrink: 0; accent-color: var(--brand-500, #6366f1); width: 15px; height: 15px; }
.sc-check__body { flex: 1; }
.sc-check__label { font-size: 0.82rem; font-weight: 600; color: var(--gray-700, #374151); }
.sc-check__desc { font-size: 0.72rem; color: var(--gray-400, #9ca3af); margin-top: 2px; line-height: 1.4; }

/* Spinner */
@keyframes sc-spin { to { transform: rotate(360deg); } }
.sc-spin { animation: sc-spin .7s linear infinite; }
</style>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore, ADMIN_API } from '../store/app.js'
import axios from 'axios'

const store = useAppStore()

const loading = ref(false)
const messages = ref([])
const search = ref('')
const filterStatus = ref('')
const selectedMsg = ref(null)
const accessLogs = ref([])
const accessLoading = ref(false)
const revokeTarget = ref(null)
const revokeReason = ref('')
const revokeLoading = ref(false)
const showCompose = ref(false)
const composeError = ref('')
const composeSuccess = ref('')
const composeSending = ref(false)
const lastPortalUrl = ref('')
const notifyOk = ref(true)

const compose = ref({
  to: '',
  subject: '',
  body: '',
  mfa_type: 'password',
  mfa_password: '',
  expires_in_hours: 24,
  max_open_count: 1,
  burn_after_read: false,
  no_download: true,
  watermark: true,
})

const stats = computed(() => {
  const s = { total: messages.value.length, active: 0, opened: 0, burned: 0, expired: 0, revoked: 0 }
  for (const m of messages.value) {
    if (m.status === 'active') s.active++
    if (m.open_count > 0) s.opened++
    if (m.status === 'burned') s.burned++
    if (m.status === 'expired') s.expired++
    if (m.status === 'revoked') s.revoked++
  }
  return s
})

const filteredMessages = computed(() => {
  return messages.value.filter(m => {
    const q = search.value.toLowerCase()
    const matchSearch = !q || m.recipient_email.toLowerCase().includes(q)
    const matchStatus = !filterStatus.value || m.status === filterStatus.value
    return matchSearch && matchStatus
  })
})

const fetchMessages = async () => {
  loading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/secure/sent`)
    messages.value = res.data.items || res.data || []
  } catch (e) {
    console.error('Failed to fetch secure messages', e)
  } finally {
    loading.value = false
  }
}

const openDetail = async (msg) => {
  selectedMsg.value = msg
  accessLoading.value = true
  accessLogs.value = []
  try {
    const res = await axios.get(`${ADMIN_API}/secure/sent/${msg.token}/accesses`)
    const raw = res.data?.items ?? res.data ?? []
    accessLogs.value = (Array.isArray(raw) ? raw : []).filter(l => l.event_type !== 'created')
  } catch (e) {
    console.error('Failed to fetch access logs', e)
  } finally {
    accessLoading.value = false
  }
}

const confirmRevoke = (msg) => {
  revokeTarget.value = msg
  revokeReason.value = ''
}

const doRevoke = async () => {
  if (!revokeTarget.value) return
  revokeLoading.value = true
  try {
    await axios.post(`${ADMIN_API}/secure/sent/${revokeTarget.value.token}/revoke`, {
      reason: revokeReason.value || null,
    })
    revokeTarget.value.status = 'revoked'
    revokeTarget.value = null
  } catch (e) {
    window.$toast?.error('撤回失敗', e.response?.data?.detail || '無法撤回安全郵件')
  } finally {
    revokeLoading.value = false
  }
}

const copyPortalUrl = (token) => {
  const msg = messages.value.find(m => m.token === token)
  const url = msg?.portal_url || `https://mail.chenya.biz/s/${token}`
  navigator.clipboard.writeText(url).catch(() => {})
}

const copyText = (text) => {
  navigator.clipboard.writeText(text).catch(() => {})
}

const sendSecure = async () => {
  composeError.value = ''
  composeSuccess.value = ''
  notifyOk.value = true
  if (!compose.value.to || !compose.value.subject || !compose.value.body) {
    composeError.value = '請填寫收件人、主旨與內文'
    return
  }
  if (!compose.value.mfa_password) {
    composeError.value = '請設定收件方密碼，並透過電話或其他管道告知收件人'
    return
  }
  composeSending.value = true
  // 確保 mail session 存在以便寫入已寄出資料夾
  await store.ensureMailSession('安全傳送')
  try {
    const res = await axios.post(`${ADMIN_API}/secure/send`, {
      to: compose.value.to,
      subject: compose.value.subject,
      body: compose.value.body,
      mfa_type: compose.value.mfa_type,
      mfa_password: compose.value.mfa_password || undefined,
      expires_in_hours: compose.value.expires_in_hours,
      max_open_count: compose.value.max_open_count,
      burn_after_read: compose.value.burn_after_read,
      no_download: compose.value.no_download,
      watermark: compose.value.watermark,
    }, {
      headers: { 'X-Mail-Session': store.mailSessionToken || '' },
    })
    lastPortalUrl.value = res.data.portal_url || `${window.location.origin}/s/${res.data.token}`
    notifyOk.value = !!res.data.notify_sent
    if (res.data.notify_sent) {
      composeSuccess.value = `安全信件已傳送！通知信已寄至 ${compose.value.to}。連結：`
    } else {
      composeSuccess.value = `安全信件已建立（通知信寄送失敗：${res.data.notify_error || '未知錯誤'}，請手動複製連結傳送給收件人）。連結：`
    }
    compose.value = { to: '', subject: '', body: '', mfa_type: 'password', mfa_password: '', expires_in_hours: 24, max_open_count: 1, burn_after_read: false, no_download: true, watermark: true }
    await fetchMessages()
  } catch (e) {
    composeError.value = e.response?.data?.detail || '傳送失敗'
  } finally {
    composeSending.value = false
  }
}

const fmtDate = (d) => {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  } catch { return d }
}

const statusLabel = (s) => ({ active: '有效', expired: '已過期', burned: '已銷毀', revoked: '已撤回' }[s] || s)
const statusBadge = (s) => ({ active: 'badge-success', expired: '', burned: 'badge-danger', revoked: 'badge-warning' }[s] || '')
const statusIconStyle = (s) => {
  const m = {
    active: { background: 'var(--color-success-bg)', color: 'var(--color-success)' },
    expired: { background: 'var(--gray-100)', color: 'var(--gray-400)' },
    burned: { background: 'var(--color-danger-bg)', color: 'var(--color-danger)' },
    revoked: { background: 'var(--color-warning-bg)', color: 'var(--color-warning)' },
  }
  return m[s] || m.expired
}
const eventLabel = (t) => ({
  otp_sent: 'OTP 已寄出', otp_verified: 'OTP 驗證通過', opened: '已開啟',
  failed_mfa: 'MFA 失敗', revoked: '已撤回', burned: '已銷毀',
}[t] || t)
const eventBadge = (t) => ({
  opened: 'badge-success', otp_verified: 'badge-info',
  failed_mfa: 'badge-danger', revoked: 'badge-warning', burned: 'badge-danger',
}[t] || '')

onMounted(fetchMessages)
</script>
